import React, { useEffect, useState } from "react";
import Button from "../Button";
import Modal from "../Modal";
import Input from "../Input";
import InputCnpjLookup from "../InputCnpjLookup";
import CompanyReceitaSummary, { receitaFromLookup } from "../CompanyReceitaSummary";
import InputSelect from "../InputSelect";
import api from "../../services/api";
import { toast } from "react-toastify";
import { renderError } from "../../helpers/errors";
import { canSeeBrands, hasPermission } from "../../services/auth";
import origins from "../../seeds/origins";
import SelectBrand from "../selects/SelectBrand";
import states from "../../seeds/states";
import axios from "axios";

// Calculado na abertura, não no import: o módulo carrega antes do login, e aí
// as permissões ainda não existem.
const getTypes = () => [
    {label: 'Cliente', value: 'customer'},
    {label: 'Transportadora', value: 'shipping'},
    ...(hasPermission('company_supplier') ? [
        {label: 'Fornecedor de gravação', value: 'customization'},
        {label: 'Fornecedor de produto', value: 'product'},
    ] : []),
    ...(hasPermission('company_billing') ? [{label: 'Faturamento', value: 'billing'}] : []),
];

/**
 * @param type tipo fixo: o campo Tipo some e a empresa sai com ele.
 * @param defaultType tipo já marcado, mas que a pessoa pode trocar. O que a
 *        permissão não oferece é descartado e volta para Cliente.
 */
export const ModalCreateCompany = ({show, close, type, defaultType, showSuccess, companyStored, level}) => {
    const [loading, setLoading] = useState(false);

    const [companyTradeName, setCompanyTradeName] = useState('');
    const [companyCompanyName, setCompanyCompanyName] = useState('');
    const [companyCnpj, setCompanyCnpj] = useState('');
    const [companyPhone, setCompanyPhone] = useState('');
    const [companyType, setCompanyType] = useState(['customer']);
    const [origin, setOrigin] = useState('');
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [companyIe, setCompanyIe] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [street, setStreet] = useState('');
    const [number, setNumber] = useState('');
    const [complement, setComplement] = useState('');
    const [district, setDistrict] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [ibgeCityCode, setIbgeCityCode] = useState('');
    const [receita, setReceita] = useState(null);

    const types = show ? getTypes() : [];

    const initialType = () => {
        const allowed = getTypes().map(option => option.value);
        const wanted = (defaultType ?? []).filter(value => allowed.includes(value));
        return wanted.length > 0 ? wanted : ['customer'];
    };

    useEffect(() => {
        if(show) setCompanyType(initialType());
        else clean();
    }, [show]);

    const fillFromCnpj = company => {
        const fill = (setter, value) => {
            if (value) setter(value);
        };

        fill(setCompanyCompanyName, company.nome);
        fill(setCompanyTradeName, company.fantasia || company.nome);
        fill(setCompanyPhone, company.telefone);
        fill(setCompanyIe, company.ie);
        fill(setZipCode, company.cep);
        fill(setStreet, company.logradouro);
        fill(setNumber, company.numero);
        fill(setComplement, company.complemento);
        fill(setDistrict, company.bairro);
        fill(setCity, company.municipio);
        fill(setState, company.uf);
        fill(setCompanyEmail, company.email);

        if (company.municipio) setIbgeCityCode(company.ibge || '');

        setReceita(receitaFromLookup(company));
    }

    const changeCnpj = value => {
        setCompanyCnpj(value);
        setReceita(null);
    }

    // O código IBGE só vale para a cidade de onde veio; cidade ou UF digitada à mão o invalida.
    const changeCity = value => {
        setCity(value);
        setIbgeCityCode('');
    }

    const changeState = value => {
        setState(value);
        setIbgeCityCode('');
    }

    // Só o CEP digitado consulta a ViaCEP: o que vem da busca por CNPJ já traz o endereço da Receita, e a ViaCEP
    // o apagaria nos CEPs de cidade inteira, que não têm logradouro.
    const changeZipCode = value => {
        setZipCode(value);

        const digits = value.replace(/\D/g, '');

        if (digits.length !== 8) return;

        axios.get(`https://viacep.com.br/ws/${digits}/json/`).then(res => {
            const address = res.data;

            if (address.erro) return;

            if (address.logradouro) setStreet(address.logradouro);
            if (address.bairro) setDistrict(address.bairro);
            if (address.localidade) setCity(address.localidade);
            if (address.uf) setState(address.uf);
            setIbgeCityCode(address.ibge ?? '');
        }).catch(() => {});
    }

    const storeCompany = () => {
        setLoading(true);

        api.post(`/company`, {
            phone: companyPhone,
            email: companyEmail,
            ie: companyIe,
            ibge_city_code: ibgeCityCode || null,
            ...(receita ? {...receita, receita_checked: true} : {}),
            zip_code: zipCode,
            street,
            number,
            complement,
            district,
            city,
            state,
            trade_name: companyTradeName,
            company_name: companyCompanyName,
            cnpj: companyCnpj,
            type: type ?? companyType,
            origin,
            ...(selectedBrands.length > 0 && { brands: selectedBrands })
        }).then(res => {
            {showSuccess !== false &&
                toast.success('Empresa cadastrada com sucesso');
            }
            if (typeof companyStored === 'function') {
                companyStored(res.data.resource);
            }
            setLoading(false);
            clean();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const clean = () => {
        setCompanyTradeName('');
        setCompanyCompanyName('');
        setCompanyCnpj('');
        setCompanyPhone('');
        setCompanyType(['customer']);
        setOrigin('');
        setSelectedBrands([]);
        setCompanyIe('');
        setCompanyEmail('');
        setIbgeCityCode('');
        setReceita(null);
        setZipCode('');
        setStreet('');
        setNumber('');
        setComplement('');
        setDistrict('');
        setCity('');
        setState('');
    }

    return (
        <Modal
                title={`Cadastrar empresa`}
                show={show}
                close={close}
                level={level}
                footer={
                    <Button 
                        type={`primary`}
                        size={`small`}
                        text={`Cadastrar`}
                        svg={`save-white`}
                        action={storeCompany}
                        loading={loading}
                    />
                }
            >
                <div className="modal-section-title">
                    <div className="left">
                        <h2>Dados cadastrais</h2>
                    </div>
                </div>
                <div className="section">
                    <div className="row">
                        <div className="col">
                            <InputCnpjLookup value={companyCnpj} change={changeCnpj} found={fillFromCnpj} />
                        </div>
                    </div>
                    <CompanyReceitaSummary receita={receita} compact={true} />
                    <div className="row">
                        <div className="col">
                            <Input type={`text`} label={`Razão social`} change={setCompanyCompanyName} value={companyCompanyName} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <Input type={`text`} label={`Nome da empresa`} change={setCompanyTradeName} value={companyTradeName} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`IE`} change={setCompanyIe} value={companyIe} />
                        </div>
                        <div className="col-6">
                            <Input type={`text`} label={`Telefone`} change={setCompanyPhone} value={companyPhone} mask={`convertToPhone`} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <Input type={`email`} label={`E-mail`} change={setCompanyEmail} value={companyEmail} />
                        </div>
                    </div>
                    {!type &&
                        <div className="row">
                            <div className="col">
                                <InputSelect label={`Tipo`} isMulti={true} options={types} change={setCompanyType} value={companyType} />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="col">
                            <InputSelect label={`Onde nos conheceu`} options={origins} change={setOrigin} value={origin} />
                        </div>
                    </div>
                    {canSeeBrands() &&
                        <div className="row">
                            <div className="col">
                                <SelectBrand
                                    isMulti={true}
                                    selectedBrand={selectedBrands}
                                    setSelectedBrand={setSelectedBrands}
                                    minSearch={3}
                                />
                            </div>
                        </div>
                    }

                </div>
                <div className="modal-section-title">
                    <div className="left">
                        <h2>Endereço cadastral</h2>
                    </div>
                </div>
                <div className="section">
                    <div className="row">
                        <div className="col-4">
                            <Input type={`text`} label={`CEP`} change={changeZipCode} value={zipCode} mask={`convertToCep`} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-8">
                            <Input type={`text`} label={`Logradouro`} change={setStreet} value={street} />
                        </div>
                        <div className="col-4">
                            <Input type={`text`} label={`Número`} change={setNumber} value={number} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Complemento`} change={setComplement} value={complement} />
                        </div>
                        <div className="col-6">
                            <Input type={`text`} label={`Bairro`} change={setDistrict} value={district} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-3">
                            <InputSelect label={`Estado`} options={states} change={changeState} value={state} />
                        </div>
                        <div className="col-6">
                            <Input type={`text`} label={`Cidade`} change={changeCity} value={city} />
                        </div>
                        <div className="col-3">
                            <Input type={`text`} label={`Código IBGE`} change={value => setIbgeCityCode(value.replace(/\D/g, '').slice(0, 7))} value={ibgeCityCode} />
                        </div>
                    </div>
                </div>
            </Modal>
    );
}

export default Modal;